import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, User } from "lucide-react";
import axios from "axios";
import { getApiUrl } from "../../config/api";

const AdminForm: React.FC = () => {
  const { schoolId, adminId } = useParams<{ schoolId: string; adminId: string }>();
  const isEdit = !!adminId;
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || "";

  const [formData, setFormData] = useState({
    name: "",
    title: "",
    email: "",
    password: ""
  });
  const [school, setSchool] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (schoolId) {
      fetchSchool();
    }
    if (isEdit && adminId && schoolId) {
      fetchAdmin();
    }
  }, [schoolId, adminId, isEdit]);

  const fetchSchool = async () => {
    try {
      const response = await axios.get(
        getApiUrl(`superadmin/schools/${schoolId}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSchool(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load school");
    }
  };

  const fetchAdmin = async () => {
    if (!adminId || !schoolId) return;

    try {
      const response = await axios.get(
        getApiUrl(`superadmin/schools/${schoolId}/admins`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const admins = response.data || [];
      const admin = admins.find((a: any) => a.id === parseInt(adminId));

      if (admin) {
        setFormData({
          name: admin.name || "",
          title: admin.title || "",
          email: admin.email || "",
          password: "" // Don't pre-fill password
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load admin");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      if (isEdit && adminId && schoolId) {
        const payload: any = {
          name: formData.name,
          email: formData.email,
          title: formData.title
        };
        // Only include password if provided
        if (formData.password) {
          payload.password = formData.password;
        }

        await axios.patch(
          getApiUrl(`superadmin/schools/${schoolId}/admins/${adminId}`),
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        if (!schoolId) {
          setError("School ID is required");
          setLoading(false);
          return;
        }

        if (!formData.password) {
          setError("Password is required for new admins");
          setLoading(false);
          return;
        }

        await axios.post(
          getApiUrl(`superadmin/schools/${schoolId}/admins`),
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      navigate(`/superadmin/schools/${schoolId}`);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to save admin");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate(`/superadmin/schools/${schoolId}`)}
            className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="dashboard-title">
              {isEdit ? "Edit Admin" : "Add New Admin"}
            </h1>
            {school && (
              <p className="dashboard-subtitle">{school.name}</p>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Admin Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title {isEdit ? "" : "*"}
              </label>
              <input
                type="text"
                list="admin-title-options"
                required={!isEdit}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <datalist id="admin-title-options">
                <option value="Mr" />
                <option value="Ms" />
                <option value="Mrs" />
                <option value="Miss" />
                <option value="Dr" />
                <option value="Prof" />
                <option value="Rev" />
              </datalist>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password {isEdit ? "(leave blank to keep current)" : "*"}
              </label>
              <input
                type="password"
                required={!isEdit}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder={isEdit ? "Enter new password or leave blank" : "Enter password"}
              />
              {isEdit && (
                <p className="mt-1 text-xs text-gray-500">
                  Only enter a password if you want to change it
                </p>
              )}
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(`/superadmin/schools/${schoolId}`)}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
            >
              <Save className="h-5 w-5 mr-2" />
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AdminForm;










